import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function parseLinearRoot(expr, variable = "x") {
  // expr w postaci "x+7" lub "x-3"
  const s = String(expr ?? "").replace(/\s+/g, "");
  const m = s.match(new RegExp(`^${variable}([+-])(\\d+)$`));
  if (!m) return null;

  const sign = m[1];
  const val = parseInt(m[2], 10);

  // x + a = 0 => x = -a
  // x - a = 0 => x =  a
  const root = sign === "+" ? -val : val;
  return root;
}

// === GENERATE TASK ===
export function generateTask() {
  const variable = "x";

  // liczby w nawiasach
  const vals = [2, 3, 4, 5, 6, 7, 8, 9];
  const a = vals[Math.floor(Math.random() * vals.length)];
  let b = vals[Math.floor(Math.random() * vals.length)];
  while (b === a) b = vals[Math.floor(Math.random() * vals.length)];

  // losujemy znaki w obu nawiasach
  const signA = Math.random() < 0.5 ? "-" : "+";
  const signB = Math.random() < 0.5 ? "-" : "+";

  const factor1 = `${variable}${signA}${a}`; // np. x-3
  const factor2 = `${variable}${signB}${b}`; // np. x+7

  // losujemy, który nawias jest w mianowniku
  const denomIsFirst = Math.random() < 0.5;
  const denominatorTex = denomIsFirst ? factor1 : factor2;

  // wynik po skróceniu = drugi czynnik
  const expected = denomIsFirst ? factor2 : factor1;

  const root = parseLinearRoot(denominatorTex, variable);
  const domainTex =
    root === null ? `${variable}\\neq ?` : `${variable}\\neq ${root}`;

  return {
    type: "CancelBracketFactorTask",
    data: {
      factor1,
      factor2,
      denominatorTex,
      expected,
      domainTex,
    },
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  const { factor1, factor2, denominatorTex, domainTex } = data;

  return (
    <div className="space-y-2">
      <p>
        Uprość wyrażenie. Załóż, że <InlineMath math={domainTex} />.
      </p>
      <BlockMath math={`\\frac{(${factor1})(${factor2})}{${denominatorTex}}`} />
    </div>
  );
}

// === RENDER INPUT ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={"np. x+5"}
      showPreview
    />
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string") {
    return { isCorrect: false, expected: data.expected };
  }

  const normalize = (str) =>
    str
      .replace(/−/g, "-")
      .replace(/\s+/g, "")
      .replace(/\*/g, "")
      .toLowerCase()
      .replace(/-/g, "+-")
      .split("+")
      .filter(Boolean)
      .sort()
      .join("+");

  return {
    isCorrect: normalize(userAnswer) === normalize(data.expected),
    expected: data.expected,
  };
}

// === RENDER ANSWER ===
export function renderAnswer({ data }) {
  return <InlineMath math={data.expected} />;
}

// === EXPLANATION ===
export function renderExplanation({ data }) {
  const { factor1, factor2, denominatorTex, expected, domainTex } = data;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> W liczniku mamy iloczyn dwóch czynników:
      </p>
      <BlockMath math={`(${factor1})(${factor2})`} />

      <p>
        <strong>Krok 2:</strong> W mianowniku jest jeden z tych czynników, więc możemy go skrócić:
      </p>
      <BlockMath
        math={`\\frac{(${factor1})(${factor2})}{${denominatorTex}} = ${expected}`}
      />

      <p className="text-sm opacity-90">
        Dziedzina wynika z warunku <InlineMath math={`${denominatorTex}\\neq 0`} />{" "}
        czyli <InlineMath math={domainTex} />.
      </p>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}
